import React from 'react'

import Capacity from 'src/comps/Capacity'
import Allotment from 'client/components/Rooms/RoomResponsive/Allotment'

import { useS4LContext } from '@s4l/react-context'
import styles from './styles.module.css'

import Price from 'client/components/Price'
import type { BookingInformation, BookingRulesRoomInformation } from '../types'
import RateNOR from 'client/components/Rooms/RoomResponsive/OfferResponsive/OfferConditions/RateNOR'
import RateNRF from 'client/components/Rooms/RoomResponsive/OfferResponsive/OfferConditions/RateNRF'
import RateSFL from 'client/components/Rooms/RoomResponsive/OfferResponsive/OfferConditions/RateSFL'
import { Typography, Button, Tag } from '@s4l/ds'
import { IconArrowDownGraph } from '@s4l/icons/IconArrowDownGraph'
import { IconArrowUpGraph } from '@s4l/icons/IconArrowUpGraph'
import DatesChangeConnected from 'src/comps/DatesChangeConnected'

interface ModifyDatesRoomProps {
  room: BookingRulesRoomInformation
  existingBooking?: BookingInformation
  isMultiroom: boolean
  offerSelected?: boolean
  onClick?: () => void
}

export enum Rate {
  NOR = 'NOR',
  SFL = 'SFL',
  NRF = 'NRF',
}

const ModifyDatesRoom = ({
  room,
  offerSelected = false,
  onClick,
  existingBooking,
}: ModifyDatesRoomProps) => {
  const { i18n } = useS4LContext()

  const { adults, children, childrenAges, roomName, roomsNumber, allotment } = room

  const rates = {
    [Rate.NOR]: RateNOR,
    [Rate.NRF]: RateNRF,
    [Rate.SFL]: RateSFL,
  }

  const RateComponent = rates[room.paymentPolicy as Rate] ?? rates[Rate.NOR]

  const rateProps = {
    cancellation: Array.isArray(room?.cancellation) ? room.cancellation[0] : room?.cancellation,
    currency: room.currency,
    payRate: room.payRate,
  }

  const tagDiscount = existingBooking?.sellingRate && room.sellingRate - existingBooking.sellingRate

  const renderDiscountTag = () => {
    if (!tagDiscount || tagDiscount === 0) {
      return null
    }

    const isNegative = tagDiscount < 0
    const Icon = isNegative ? IconArrowDownGraph : IconArrowUpGraph

    return (
      <Tag
        size="m"
        icon={<Icon />}
        bgColor={isNegative ? 'var(--color-success-500)' : 'var(--color-error-500)'}
      >
        <Price amount={tagDiscount} currency={room.currency} />
      </Tag>
    )
  }

  return (
    <div className={styles.container} data-testid="room">
      <div className={styles.roomContainer}>
        {!offerSelected && (
          <div className={styles.detailsContainer}>
            <div className={styles.roomHeader}>
              <h3 className={styles.roomName}>
                {roomsNumber > 1 ? `(x${roomsNumber}) ` : ''}
                {roomName?.charAt(0).toUpperCase() + roomName?.slice(1)}
              </h3>
              <div className={styles.detailsCapacityContainer}>
                <Capacity
                  adults={adults}
                  children={children}
                  childrenAges={childrenAges}
                  showAges={false}
                  title={false}
                  showIcons={false}
                />
              </div>
              <Allotment allotment={allotment} />
            </div>
          </div>
        )}
        <div className={styles.offersContainer}>
          <div className={styles.boardContainer}>
            <div className={styles.offerConditionsContainer}>
              <ul className={styles.offerConditionsList}>
                <RateComponent {...rateProps} />
              </ul>
            </div>
            <div
              className={styles.priceBookingContainer}
              data-testid="room-prices"
              data-availability-id={room.availabilityId}
              data-currency={room.currency}
              data-total-price={room.sellingRate}
            >
              {existingBooking && (
                <div className={styles.priceContainer}>
                  <Typography variant="body" size="l">
                    {i18n.formatMessage({ id: 'change-dates.original-price' })}
                  </Typography>
                  <Typography variant="body" size="xl" strike>
                    <Price
                      amount={existingBooking.sellingRate}
                      currency={existingBooking.currency}
                    />
                  </Typography>
                </div>
              )}
              <div className={styles.priceContainer}>
                <span className={styles.priceLabel}>
                  <Typography variant="body" size="l">
                    {i18n.formatMessage({ id: 'change-dates.new-price' })}
                  </Typography>
                  {renderDiscountTag()}
                </span>
                <Typography variant="body" size="xl" bold>
                  <Price amount={room.sellingRate} currency={room.currency} />
                </Typography>
              </div>
            </div>
            {!offerSelected && (
              <div className={styles.bookingButtonContainer}>
                <DatesChangeConnected
                  trackingData={{
                    context: 'booking_thanks',
                    details: 'Select offer',
                  }}
                >
                  <Button
                    data-testId="button-modify-booking"
                    onClick={onClick}
                    variant="filled-rounded"
                    size="small"
                  >
                    {i18n.formatMessage({ id: 'global.select-offer-modify-dates' })}
                  </Button>
                </DatesChangeConnected>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModifyDatesRoom
